import React from 'react';
import styled from 'styled-components';
import { useCareerState } from '../../shared/hooks/useApi/useCareer';
import {
  AboutContentMode,
  CareerPeriodType,
  CareerType,
  DividerType,
  ModalType,
} from '../../shared/enum/EnumRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useAboutViewStore } from '../../shared/store/aboutViewStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { DeviceType } from '../../shared/enum/EnumRepository';
import reorderIcon from '../../asset/icon/reorder.png';
import Divider from '../../shared/Divider';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import CareerInfo from './CareerInfo';
import { CareerData } from '../../shared/dto/EntityRepository';

const visibleYearKey = (
  career: CareerData,
  asPeriod: boolean,
  isExtended: boolean,
): string => {
  const start = isExtended
    ? [career.startYear, career.startMonth, career.startDay].filter(Boolean).join('.')
    : career.startYear;
  if (!asPeriod || career.periodType !== CareerPeriodType.PERIOD) return start;
  if (career.isCurrent) return `${start}~`;
  const end = isExtended
    ? [career.endYear, career.endMonth, career.endDay].filter(Boolean).join('.')
    : career.endYear;
  return `${start}~${end}`;
};

interface CareerSectionProps {
  title: string;
  secondaryTitle?: string;
  type: CareerType;
  storageRange: CareerPeriodType;
  displayRange: CareerPeriodType;
  contentMode: AboutContentMode;
  secondaryContentMode?: AboutContentMode;
  showControls: boolean;
  fullWidth: boolean;
}

const CareerSection: React.FC<CareerSectionProps> = ({
  title,
  secondaryTitle,
  type,
  storageRange,
  displayRange,
  contentMode,
  secondaryContentMode,
  showControls,
  fullWidth,
}) => {
  const { isEditMode } = useEditMode();
  const { isExtended } = useAboutViewStore();
  const { careerList } = useCareerState();
  const { setStandardModal } = useModalStore();
  const { deviceType } = useDeviceStore();
  const filteredCareers = careerList.filter((each) => each.careerType === type);
  const hasChangedCareer = filteredCareers.some((career) => career.hasChanged);
  const usePeriodInfo =
    displayRange === CareerPeriodType.PERIOD ||
    (isExtended && storageRange === CareerPeriodType.PERIOD);

  if (filteredCareers.length <= 0) {
    return null;
  }

  const handleReOrder = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: "Career",
      value: type,
      handleChange: () => { },
    });
  }

  const renderHeader = (
    controlsVisible: boolean,
    reserveControls: boolean = false,
    headerTitle: string = title,
  ) => (
    <SectionHeader>
      <TitleContainer
        data-has-changed={controlsVisible && hasChangedCareer ? "true" : undefined}
      >
        <CareerTitle>{headerTitle}</CareerTitle>
        {isEditMode && (controlsVisible || reserveControls) && (
          <ReservedControl $hidden={!controlsVisible} aria-hidden={!controlsVisible}>
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent
              backgroundImg={reorderIcon}
              onClick={handleReOrder}
            />
          </ReservedControl>
        )}
      </TitleContainer>
      {/* <Divider dividerType={DividerType.PLAIN} bottom={'8px'} /> */}
    </SectionHeader>
  );

  if (secondaryContentMode) {
    return (
      <PairedSection>
        {renderHeader(showControls)}
        {renderHeader(false, true, secondaryTitle ?? title)}
        {filteredCareers.map((each, index) => {
          const asPeriod = usePeriodInfo && each.periodType === CareerPeriodType.PERIOD;
          const previous = filteredCareers[index - 1];
          const currentKey = visibleYearKey(each, asPeriod, isExtended);
          const previousKey = previous
            ? visibleYearKey(
              previous,
              usePeriodInfo && previous.periodType === CareerPeriodType.PERIOD,
              isExtended,
            )
            : '';
          const hideYear = !isEditMode && currentKey !== '' && currentKey === previousKey;
          return (
            <React.Fragment key={each.id}>
              <CareerInfo
                data={each}
                displayAsPeriod={asPeriod}
                contentMode={contentMode}
                showControls={showControls}
                hideYear={hideYear}
                showCurrentToggle={displayRange === CareerPeriodType.PERIOD}
              />
              <CareerInfo
                data={each}
                displayAsPeriod={asPeriod}
                contentMode={secondaryContentMode}
                showControls={false}
                reserveControls
                englishOnly
                hideYear={hideYear}
              />
            </React.Fragment>
          );
        })}
      </PairedSection>
    );
  }

  return (
    <Section
      $isMobile={deviceType === DeviceType.MOBILE}
      $fullWidth={fullWidth}
    >
      {renderHeader(showControls)}
      {filteredCareers.map((each, index) => {
        const asPeriod = usePeriodInfo && each.periodType === CareerPeriodType.PERIOD;
        const previous = filteredCareers[index - 1];
        const currentKey = visibleYearKey(each, asPeriod, isExtended);
        const previousKey = previous
          ? visibleYearKey(
            previous,
            usePeriodInfo && previous.periodType === CareerPeriodType.PERIOD,
            isExtended,
          )
          : '';
        const hideYear = !isEditMode && currentKey !== '' && currentKey === previousKey;
        return (
        <CareerInfo
          key={each.id}
          data={each}
          displayAsPeriod={asPeriod}
          contentMode={contentMode}
          showControls={showControls}
          hideYear={hideYear}
          showCurrentToggle={displayRange === CareerPeriodType.PERIOD}
        />
        );
      })}
    </Section>
  );
}

const PairedSection = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 600px));
  column-gap: clamp(100px, 8vw, 150px);
  align-items: start;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const SectionHeader = styled.div`
  min-width: 0;
`;

const Section = styled.div<{ $isMobile: boolean; $fullWidth: boolean }>`
  position: relative;
  width: ${({ $isMobile, $fullWidth }) =>
    $isMobile || $fullWidth ? '100%' : 'clamp(320px, 65vw, 600px)'};
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2vw;
  margin-bottom: 8px;
`;

const ReservedControl = styled.span<{ $hidden: boolean }>`
  display: inline-flex;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
`;

const CareerTitle = styled.h3`
  ${({ theme }) => theme.typography.Body_04};
  font-weight: 600;
`;

export default CareerSection;
